import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "statusCheckbox", "connectivityCheckbox", "deviceTypeCheckbox",
    "networkGroup", "networkCount", "deviceCount",
    "activeIndicator", "filterButton",
  ]

  activeStatuses = new Set()
  activeConnectivity = new Set()
  activeDeviceTypes = new Set()

  toggleStatus(event) {
    const status = event.target.dataset.status
    if (event.target.checked) {
      this.activeStatuses.add(status)
    } else {
      this.activeStatuses.delete(status)
    }
  }

  toggleConnectivity(event) {
    const connectivity = event.target.dataset.connectivity
    if (event.target.checked) {
      this.activeConnectivity.add(connectivity)
    } else {
      this.activeConnectivity.delete(connectivity)
    }
  }

  toggleDeviceType(event) {
    const type = event.target.dataset.deviceType
    if (event.target.checked) {
      this.activeDeviceTypes.add(type)
    } else {
      this.activeDeviceTypes.delete(type)
    }
  }

  apply() {
    this.filterDevices()
  }

  clearAll() {
    this.activeStatuses.clear()
    this.activeConnectivity.clear()
    this.activeDeviceTypes.clear()

    this.statusCheckboxTargets.forEach(cb => cb.checked = false)
    this.connectivityCheckboxTargets.forEach(cb => cb.checked = false)
    this.deviceTypeCheckboxTargets.forEach(cb => cb.checked = false)
    this.filterDevices()
  }

  /**
   * Closes the Radar devices filter dropdown (nested `dropdown` controller) on Escape.
   * Scoped to this controller only; see `devices_list` + `data-action` wiring.
   */
  closeFilterDropdownFromEscape(event) {
    if (event.key !== "Escape") return

    const menu = this.element.querySelector("[data-dropdown-target=\"menu\"]")
    if (!menu || menu.classList.contains("hidden")) return

    const dropdownEl = menu.closest(".dropdown")
    if (!dropdownEl || !this.element.contains(dropdownEl)) return

    const dropdownController = this.application.getControllerForElementAndIdentifier(
      dropdownEl,
      "dropdown"
    )
    if (!dropdownController) return

    event.preventDefault()
    event.stopImmediatePropagation()
    dropdownController.hide()
  }

  filterDevices() {
    const hasStatusFilter = this.activeStatuses.size > 0
    const hasConnectivityFilter = this.activeConnectivity.size > 0
    const hasTypeFilter = this.activeDeviceTypes.size > 0
    let totalVisible = 0

    this.networkGroupTargets.forEach((group, index) => {
      const devices = group.querySelectorAll("[data-radar-device-selector-target='device']")
      let networkVisible = 0

      devices.forEach(device => {
        const status = device.dataset.status
        const connectivity = device.dataset.connectivity
        const deviceType = device.dataset.deviceType

        const statusMatch = !hasStatusFilter || this.activeStatuses.has(status)
        const connectivityMatch = !hasConnectivityFilter || this.activeConnectivity.has(connectivity)
        const typeMatch = !hasTypeFilter || this.activeDeviceTypes.has(deviceType)

        if (statusMatch && connectivityMatch && typeMatch) {
          device.style.display = ""
          networkVisible++
        } else {
          device.style.display = "none"
        }
      })

      totalVisible += networkVisible

      const networkCountEl = this.networkCountTargets.find(el =>
        el.dataset.networkIndex === String(index)
      )
      if (networkCountEl) {
        networkCountEl.textContent = `(${networkVisible})`
      }
    })

    if (this.hasDeviceCountTarget) {
      this.deviceCountTarget.textContent = `(${totalVisible})`
    }

    this.updateActiveIndicator()
  }

  updateActiveIndicator() {
    const count = this.activeStatuses.size + this.activeConnectivity.size + this.activeDeviceTypes.size

    if (this.hasActiveIndicatorTarget) {
      if (count > 0) {
        this.activeIndicatorTarget.classList.remove("hidden")
      } else {
        this.activeIndicatorTarget.classList.add("hidden")
      }
    }

    if (this.hasFilterButtonTarget) {
      const btn = this.filterButtonTarget
      const defaultLabel = btn.dataset.defaultAriaLabel
      const activeLabel = btn.dataset.activeAriaLabel
      if (defaultLabel && activeLabel) {
        btn.setAttribute("aria-label", count > 0 ? activeLabel : defaultLabel)
      }
    }
  }
}
